import Interceptor from "./Interceptor";

// ==================== SSH Key Management ====================

/**
 * Generate new SSH key pair
 * @param {Object} payload - { keyName, comment }
 * @returns {Promise}
 */
export const generateSshKey = async (payload) => {
  try {
    const response = await Interceptor.post("/git/ssh-keys", payload);
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Get all SSH keys for the user
 * @returns {Promise}
 */
export const getAllSshKeys = async () => {
  try {
    const response = await Interceptor.get("/git/ssh-keys");
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Get SSH key details by ID
 * @param {string} keyId
 * @returns {Promise}
 */
export const getSshKeyById = async (keyId) => {
  try {
    const response = await Interceptor.get(`/git/ssh-keys/${keyId}`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Delete SSH key
 * @param {string} keyId
 * @returns {Promise}
 */
export const deleteSshKey = async (keyId) => {
  try {
    const response = await Interceptor.delete(`/git/ssh-keys/${keyId}`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

// ==================== Git Configuration ====================

/**
 * Create new Git configuration
 * @param {Object} payload - Git config data
 * @returns {Promise}
 */
export const createGitConfig = async (payload) => {
  try {
    const response = await Interceptor.post("/git/config", payload);
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Get Git configurations for a site
 * @param {string} siteId
 * @returns {Promise}
 */
export const getGitConfigsBySite = async (siteId) => {
  try {
    const response = await Interceptor.get(`/git/config/${siteId}`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Update Git configuration
 * @param {string} configId
 * @param {Object} payload
 * @returns {Promise}
 */
export const updateGitConfig = async (configId, payload) => {
  try {
    const response = await Interceptor.put(`/git/config/${configId}`, payload);
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Delete Git configuration
 * @param {string} configId
 * @returns {Promise}
 */
export const deleteGitConfig = async (configId) => {
  try {
    const response = await Interceptor.delete(`/git/config/${configId}`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Test Git configuration connection
 * @param {string} configId
 * @returns {Promise}
 */
export const testGitConnection = async (configId) => {
  try {
    const response = await Interceptor.post(`/git/config/${configId}/test`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

// ==================== Deployment Pre-checks ====================

/**
 * Run deployment pre-checks
 * @param {string} configId
 * @returns {Promise}
 */
export const runDeploymentPrecheck = async (configId) => {
  try {
    const response = await Interceptor.get(`/git/precheck/${configId}`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

// ==================== Deployment Management ====================

/**
 * Trigger manual deployment
 * @param {string} configId
 * @param {Object} payload - { commitHash, branch, environment, triggeredBy }
 * @returns {Promise}
 */
export const triggerDeployment = async (configId, payload = {}) => {
  try {
    const response = await Interceptor.post(`/git/deploy/${configId}`, payload);
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Get deployments for a site
 * @param {string} siteId
 * @param {Object} params - { page, limit, status, environment, branch }
 * @returns {Promise}
 */
export const getDeploymentsBySite = async (siteId, params = {}) => {
  try {
    const response = await Interceptor.get(`/git/deployments/${siteId}`, {
      params,
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Get deployment details
 * @param {string} deploymentId
 * @returns {Promise}
 */
export const getDeploymentById = async (deploymentId) => {
  try {
    const response = await Interceptor.get(
      `/git/deployments/detail/${deploymentId}`,
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Rollback deployment
 * @param {string} deploymentId
 * @returns {Promise}
 */
export const rollbackDeployment = async (deploymentId) => {
  try {
    const response = await Interceptor.post(
      `/git/deployments/${deploymentId}/rollback`,
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

// ==================== Deployment Queue ====================

/**
 * Get deployment queue for a site
 * @param {string} siteId
 * @returns {Promise}
 */
export const getDeploymentQueue = async (siteId) => {
  try {
    const response = await Interceptor.get(`/git/queue/${siteId}`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Get all deployment queues (admin only)
 * @returns {Promise}
 */
export const getAllDeploymentQueues = async () => {
  try {
    const response = await Interceptor.get("/git/queue/all");
    return response.data;
  } catch (error) {
    throw error;
  }
};

// ==================== Repository Information ====================

/**
 * Get repository branches
 * @param {string} configId
 * @returns {Promise}
 */
export const getRepositoryBranches = async (configId) => {
  try {
    const response = await Interceptor.get(`/git/repo/${configId}/branches`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Get repository commits
 * @param {string} configId
 * @param {Object} params - { branch, limit }
 * @returns {Promise}
 */
export const getRepositoryCommits = async (configId, params = {}) => {
  try {
    const response = await Interceptor.get(`/git/repo/${configId}/commits`, {
      params,
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Sync local changes to Git (push SFTP/WP-Admin changes to GitHub)
 * @param {string} configId
 * @param {Object} payload - { commitMessage?: string }
 * @returns {Promise}
 */
export const syncToGit = async (configId, payload = {}) => {
  try {
    const response = await Interceptor.post(
      `/git/configs/${configId}/sync`,
      payload,
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Check for file conflicts
 * @param {string} configId
 * @returns {Promise}
 */
export const checkConflicts = async (configId) => {
  try {
    const response = await Interceptor.get(
      `/git/configs/${configId}/conflicts`,
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Resolve a deployment conflict
 * @param {string} deploymentId
 * @param {string} action - "force_deploy" | "sync_first" | "cancel"
 * @returns {Promise}
 */
export const resolveConflict = async (deploymentId, action) => {
  try {
    const response = await Interceptor.post(
      `/git/deployments/${deploymentId}/resolve`,
      { action },
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};
